import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ChartLineUp, Robot, Package, Calculator,
  UsersThree, PaperPlaneTilt, ShieldCheck, Lightning, CheckCircle,
  ArrowRight, Receipt,
} from "@phosphor-icons/react";

const features = [
  { icon: Receipt, title: "Pajak Terintegrasi", desc: "Kelola urusan pajak lebih mudah dalam satu platform." },
  { icon: Calculator, title: "Akuntansi Otomatis", desc: "Journal entries, invoice, laporan L/R dan Neraca terupdate real-time." },
  { icon: Package, title: "Inventaris Real-time", desc: "Stok, gudang, dan pergerakan barang otomatis terpotong saat penjualan." },
  { icon: ChartLineUp, title: "Dashboard Unified", desc: "KPI silang modul: revenue, deals, stok kritis dalam satu layar." },
  { icon: Robot, title: "AI Bookkeeping", desc: "Chat dengan Claude/GPT/Gemini untuk categorize transaksi dan analisis keuangan." },
  { icon: PaperPlaneTilt, title: "Telegram Bot", desc: "Catat transaksi & terima laporan bisnis langsung dari Telegram." },
];

const plans = [
  { id: "starter", name: "Starter", price: "149.000", features: ["1 workspace", "CRM + Akuntansi", "AI Chat 100 pesan/bulan", "Community support"] },
  { id: "pro", name: "Professional", price: "499.000", featured: true, features: ["3 workspace", "Semua modul ERP", "AI Chat unlimited", "Telegram Bot", "Priority support"] },
  { id: "business", name: "Business", price: "1.499.000", features: ["Workspace unlimited", "Semua modul + API", "AI + Telegram + Xendit live", "Dedicated support"] },
];

const rupiahFmt = (n) => (Number(n || 0)).toLocaleString("id-ID", { maximumFractionDigits: 0 });

export default function Landing() {
  const [plans, setPlans] = React.useState([]);
  const [periods, setPeriods] = React.useState([{ months: 1, label: "Bulanan", discount_pct: 0 }]);
  const [selectedMonths, setSelectedMonths] = React.useState(1);
  const [featureLabels, setFeatureLabels] = React.useState({});
  const [loadingPlans, setLoadingPlans] = React.useState(true);

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/public-plans`)
      .then((r) => r.json())
      .then((d) => {
        setPlans(d.plans || []);
        const bps = d.billing_periods?.length ? d.billing_periods : [{ months: 1, label: "Bulanan", discount_pct: 0 }];
        setPeriods(bps);
        if (!bps.find((b) => b.months === selectedMonths)) setSelectedMonths(bps[0].months);
        // Build union of feature labels dari semua paket (urut unik dari paket tertinggi)
        const seen = new Set();
        const labels = {};
        for (const p of d.plans || []) {
          for (const f of p.features_enabled || []) {
            if (!seen.has(f)) { seen.add(f); labels[f] = f; }
          }
        }
        setFeatureLabels(labels);
      })
      .catch(() => setPlans([]))
      .finally(() => setLoadingPlans(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activePeriod = periods.find((b) => b.months === selectedMonths) || periods[0];
  const calcAmount = (price) => {
    const gross = price * (activePeriod?.months || 1);
    return Math.round(gross * (1 - (activePeriod?.discount_pct || 0) / 100));
  };

  return (
    <div className="min-h-screen bg-[#FDFDFC]" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="nav-brand">
            <span className="font-serif-display text-3xl font-semibold text-slate-900">AIAkuntan</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#features" className="hover:text-slate-900 transition-colors">Fitur</a>
            <a href="#pricing" className="hover:text-slate-900 transition-colors">Harga</a>
            <a href="#ai" className="hover:text-slate-900 transition-colors">AI + Telegram</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900" data-testid="nav-login">Masuk</Link>
            <Link to="/register" data-testid="nav-register">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-md">Mulai Gratis</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-bg relative overflow-hidden grain-overlay">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs text-slate-600 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-600" />
              Baru dirilis: AI Bookkeeping via Telegram
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-medium text-slate-900 leading-[1.05] tracking-tight">
              Pekerjaan Akuntan<br />
              <span className="text-amber-700 italic">Dikerjaan</span> oleh AI.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Kamu fokus mengembangkan Bisnis, dibelakang layar AI Akuntan yang urus.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/register" data-testid="hero-cta-primary">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white rounded-md h-12 px-6 text-base">
                  Mulai Trial 14 Hari <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <a href="#features" data-testid="hero-cta-secondary">
                <Button variant="outline" size="lg" className="rounded-md h-12 px-6 text-base border-slate-300">
                  Lihat Fitur Lengkap
                </Button>
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
              <div className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" />Tanpa kartu kredit</div>
              <div className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" />Setup 5 menit</div>
              <div className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" />Xendit ready</div>
            </div>
          </div>
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative rounded-lg overflow-hidden border border-slate-200 shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1775046318523-b5227f41fe97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Business owner"
                className="w-full h-[480px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-lg shadow-md p-4 w-64">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Laba Bulan Ini</div>
              <div className="font-serif-display text-3xl font-semibold text-slate-900 mt-1 tabular-nums">Rp 47,2 jt</div>
              <div className="text-xs text-emerald-600 mt-1 flex items-center gap-1">↗ +23% vs bulan lalu</div>
            </div>
            <div className="absolute -top-4 -right-4 bg-slate-900 text-white rounded-lg shadow-md p-4 w-56">
              <div className="flex items-center gap-2 text-xs text-amber-400 uppercase tracking-wider">
                <Robot size={14} weight="fill" /> AI Assist
              </div>
              <div className="text-sm mt-2 leading-relaxed">
                "Transaksi Rp 2,5jt untuk 'sewa gudang' — akan saya masukkan ke akun 6200."
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-amber-700 mb-3">Semua yang bisnis butuhkan</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl font-medium text-slate-900 leading-tight">Modul terintegrasi, tanpa mahal.</h2>
            <p className="mt-4 text-slate-600 text-base leading-relaxed">Berbeda dengan Odoo yang mahal & rumit, AIAkuntan dibuat khusus untuk Owner yang ingin pencatatan serba cepat dan otomatis dibantu oleh AI</p>
          </div>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, desc }, i) => (
              <div key={i} data-testid={`feature-${i}`}
                className="group bg-white border border-slate-200 rounded-md p-6 hover:-translate-y-0.5 hover:border-slate-300 transition-transform duration-200"
              >
                <div className="w-10 h-10 rounded-md bg-slate-900 text-amber-500 flex items-center justify-center mb-4">
                  <Icon size={20} weight="duotone" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI section */}
      <section id="ai" className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-400 mb-3">AI + Telegram</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl font-medium leading-tight">
              Pembukuan cerdas.<br /><span className="italic text-amber-400">Di ujung jari.</span>
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-lg">
              Tanya AI tentang keuangan bisnismu, catat pengeluaran via chat Telegram, atau terima laporan otomatis setiap pagi. Pilih model Claude, GPT, atau Gemini sesuai preferensi.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-slate-300">
              {["Kategorisasi transaksi otomatis", "Laporan L/R & arus kas via chat", "Telegram Bot: /beban 50000 Makanan", "3 model AI pilihan"].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-amber-400 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
            <div className="space-y-3 text-sm">
              <div className="flex justify-end">
                <div className="bg-amber-600 text-white px-4 py-2 rounded-lg max-w-xs">
                  Berapa laba bersih bulan ini?
                </div>
              </div>
              <div className="flex justify-start">
                <div className="bg-slate-700 text-slate-100 px-4 py-2 rounded-lg max-w-md">
                  <div className="text-xs text-amber-400 mb-1">Claude Sonnet 4.6</div>
                  Laba bersih bulan ini <span className="font-semibold">Rp 47.200.000</span>, naik 23% dari bulan lalu. Top expense: Gaji (Rp 18jt) dan Sewa (Rp 8jt).
                </div>
              </div>
              <div className="flex justify-end">
                <div className="bg-amber-600 text-white px-4 py-2 rounded-lg max-w-xs">
                  Catat beban listrik Rp 850rb
                </div>
              </div>
              <div className="flex justify-start">
                <div className="bg-slate-700 text-slate-100 px-4 py-2 rounded-lg max-w-md">
                  Beban listrik Rp 850.000 saya masukkan ke akun <span className="font-semibold">6300 - Utilitas</span>. Sudah tercatat di jurnal.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-amber-700 mb-3">Harga</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl font-medium text-slate-900">Bayar bulanan atau tahunan. Batalkan kapan saja.</h2>
            <p className="mt-4 text-slate-600">Semua paket sudah termasuk trial 14 hari. Hemat lebih banyak dengan paket tahunan.</p>
          </div>
          {periods.length > 1 && (
            <div className="mt-8 inline-flex rounded-full border border-slate-300 bg-white p-1 shadow-sm" data-testid="landing-period-switcher">
              {periods.map((bp) => (
                <button key={bp.months}
                  onClick={() => setSelectedMonths(bp.months)}
                  className={`relative px-5 py-2 text-sm rounded-full transition-colors ${selectedMonths === bp.months ? "bg-slate-900 text-white shadow" : "text-slate-700 hover:bg-slate-50"}`}
                  data-testid={`landing-period-${bp.months}m`}>
                  {bp.label}
                  {bp.discount_pct > 0 && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white tabular-nums font-medium">Hemat {bp.discount_pct}%</span>}
                </button>
              ))}
            </div>
          )}
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-grid">
            {loadingPlans && <div className="col-span-full text-center text-slate-400 py-10">Memuat paket...</div>}
            {!loadingPlans && plans.length === 0 && <div className="col-span-full text-center text-slate-400 py-10">Belum ada paket.</div>}
            {plans.map((p) => {
              const featured = p.id === "pro";
              const isFree = p.id === "free";
              const amount = calcAmount(p.price);
              const perMonth = Math.round(amount / (activePeriod?.months || 1));
              const gross = p.price * (activePeriod?.months || 1);
              const hasPeriodDiscount = !isFree && activePeriod?.months > 1 && activePeriod?.discount_pct > 0;
              return (
                <div key={p.id} data-testid={`plan-${p.id}`}
                     className={`rounded-md p-7 border relative flex flex-col ${featured ? "bg-slate-900 text-white border-slate-900 lg:-translate-y-4" : "bg-white border-slate-200"}`}>
                  {featured && <div className="absolute -top-3 left-8 bg-amber-600 text-white text-xs px-3 py-1 rounded-full uppercase tracking-wider">Paling Populer</div>}
                  <div className={`text-sm uppercase tracking-wider ${featured ? "text-amber-400" : "text-slate-500"}`}>{p.name}</div>
                  {p.description && <p className={`text-xs mt-2 ${featured ? "text-slate-300" : "text-slate-500"}`}>{p.description}</p>}
                  <div className="mt-4">
                    {hasPeriodDiscount && (
                      <div className={`text-sm line-through ${featured ? "text-slate-500" : "text-slate-400"}`}>Rp {rupiahFmt(gross)}</div>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="font-serif-display text-4xl font-semibold" data-testid={`landing-amount-${p.id}`}>
                        {isFree ? "Gratis" : `Rp ${rupiahFmt(amount)}`}
                      </span>
                      {!isFree && <span className={featured ? "text-slate-400 text-sm" : "text-slate-500 text-sm"}>/{activePeriod?.label?.toLowerCase() || "bulan"}</span>}
                    </div>
                    {!isFree && activePeriod?.months > 1 && (
                      <div className={`text-xs mt-1 ${featured ? "text-slate-400" : "text-slate-500"}`}>Setara Rp {rupiahFmt(perMonth)}/bulan</div>
                    )}
                    {hasPeriodDiscount && (
                      <div className={`text-xs mt-1 font-medium ${featured ? "text-amber-300" : "text-emerald-700"}`}>Hemat {activePeriod.discount_pct}% dengan paket {activePeriod.label.toLowerCase()}</div>
                    )}
                  </div>
                  <Link to="/register" data-testid={`plan-cta-${p.id}`}>
                    <Button className={`w-full mt-5 rounded-md h-11 ${featured ? "bg-amber-600 hover:bg-amber-700 text-white" : isFree ? "bg-white hover:bg-slate-50 text-slate-900 border border-slate-300" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                      {isFree ? "Mulai Gratis" : "Pilih Paket"}
                    </Button>
                  </Link>
                  <div className={`mt-6 text-xs uppercase tracking-wider ${featured ? "text-slate-400" : "text-slate-500"}`}>
                    {p.feature_count} modul aktif
                  </div>
                  <ul className="mt-3 space-y-2 text-sm flex-1">
                    {p.features_enabled.slice(0, 8).map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle size={14} weight="fill" className={featured ? "text-amber-400 mt-0.5 shrink-0" : "text-emerald-600 mt-0.5 shrink-0"} />
                        <span className={featured ? "text-slate-200" : "text-slate-700"}>{f}</span>
                      </li>
                    ))}
                    {p.features_enabled.length > 8 && (
                      <li className={`text-xs italic ${featured ? "text-slate-400" : "text-slate-500"} pl-6`}>
                        + {p.features_enabled.length - 8} modul lainnya
                      </li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Tabel komparasi fitur */}
          {plans.length > 1 && Object.keys(featureLabels).length > 0 && (
            <div className="mt-20" data-testid="feature-comparison">
              <div className="max-w-2xl mb-6">
                <div className="text-xs uppercase tracking-widest text-amber-700 mb-2">Komparasi Fitur</div>
                <h3 className="font-serif-display text-3xl lg:text-4xl font-medium text-slate-900">Pilih yang sesuai kebutuhan bisnis Anda</h3>
                <p className="mt-2 text-sm text-slate-600">Semua fitur per paket. Semakin tinggi paket, semakin lengkap modulnya.</p>
              </div>
              <div className="overflow-x-auto bg-white border border-slate-200 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-700 sticky left-0 bg-slate-50 min-w-[200px]">Fitur</th>
                      {plans.map((p) => (
                        <th key={p.id} className="text-center px-4 py-3 font-medium" data-testid={`compare-head-${p.id}`}>
                          <div className={p.id === "pro" ? "text-amber-700" : "text-slate-900"}>{p.name}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums">{p.price === 0 ? "Gratis" : `Rp ${rupiahFmt(p.price)}/bulan`}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(featureLabels).map((f, idx) => (
                      <tr key={f} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"}>
                        <td className="px-4 py-2.5 text-slate-700 sticky left-0 bg-inherit">{featureLabels[f]}</td>
                        {plans.map((p) => (
                          <td key={p.id} className="text-center px-4 py-2.5" data-testid={`compare-${p.id}-${f}`}>
                            {(p.features_enabled || []).includes(f)
                              ? <CheckCircle size={16} weight="fill" className="inline text-emerald-600" />
                              : <span className="text-slate-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="bg-slate-900 text-white">
                      <td className="px-4 py-3 font-medium">Total modul aktif</td>
                      {plans.map((p) => (
                        <td key={p.id} className="text-center px-4 py-3 font-semibold tabular-nums">{p.feature_count}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-5 text-center">
                <Link to="/register">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white rounded-md h-12 px-6" data-testid="compare-cta">
                    Mulai Trial 14 Hari Gratis <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-14 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-sm text-slate-500">
          <div>
            <div className="font-serif-display text-2xl text-slate-900">AIAkuntan</div>
            <div className="mt-1">ERP terpadu untuk pemilik bisnis Nusantara.</div>
          </div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-slate-900 transition-colors">Kontak</a>
            <a href="#" className="hover:text-slate-900 transition-colors">Kebijakan Privasi</a>
            <a href="#" className="hover:text-slate-900 transition-colors">Syarat & Ketentuan</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
